import {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {workspaceApi} from '../workspace/workspaceApi.js'
import {verifyOriginal} from '../workspace/originalVerification.js'
import styles from './championToClose.module.css'

function SavedArtwork({row, onChange}) {
  const [preview, setPreview] = useState({}), [requested, setRequested] = useState(false)
  useEffect(() => {
    if (!requested || !row.output) return undefined
    let active = true, url
    workspaceApi.asset(row.jobId, row.output.id).then(async blob => {
      await verifyOriginal(blob, row.output.sha256)
      if (!active) return
      url = URL.createObjectURL(blob);setPreview({url})
    }).catch(() => {if (active) setPreview({error:'The saved original could not be verified or read. No attachment was selected.'})})
    return () => {active=false;if(url)URL.revokeObjectURL(url)}
  }, [requested, row.jobId, row.output])
  const supported = ['image/png','image/jpeg','image/webp','application/pdf'].includes(row.output?.type)
  return <article aria-label={`Existing artwork: ${row.title}`}>
    <strong>{row.output?.name || row.title}</strong>
    <p>Job {row.jobId} · Engine revision {row.revision ?? 'not prepared'}{row.output && ` · File revision ${row.output.engineRevision}`} · Provenance: {row.provenance}</p>
    <p>Assigned rep: {row.ownerId || 'Unassigned'} · Uploaded by: {row.uploader}</p>
    <p role="status">{row.reason}</p>
    {row.output && supported && <button className="btn" onClick={()=>setRequested(true)} disabled={requested}>Preview saved artwork</button>}
    {preview.url && (row.output.type.startsWith('image/') ? <img className={styles.attachmentPreview} src={preview.url} alt={`Saved artwork: ${row.output.name}`}/> : <a href={preview.url} target="_blank" rel="noreferrer">Preview saved PDF</a>)}
    {preview.error && <p role="alert">{preview.error}</p>}
    {row.eligible && <button className="btn" onClick={()=>onChange(row.key)}>Use existing artwork</button>}
    <Link className="btn" to={row.reviewUrl}>Optional artwork review</Link>{' '}
    <Link className="btn" to={row.addUrl}>Add artwork to this job</Link>
  </article>
}

export default function OutreachArtwork({options, selected, value, onChange, onReady, loading=false, context, error, onRetry}) {
  const [preview, setPreview] = useState({})
  const jobId=selected?.jobId, assetId=selected?.output.id, key=selected?.key, sha256=selected?.output.sha256
  useEffect(() => {
    let alive=true, url
    onReady(null);setPreview({})
    if(!jobId || !assetId)return undefined
    workspaceApi.asset(jobId,assetId).then(async blob=>{
      await verifyOriginal(blob, sha256)
      if(!alive)return
      url=URL.createObjectURL(blob);setPreview({url,key});onReady(key)
    }).catch(()=>{if(alive)setPreview({error:'The saved artwork could not be previewed. Refresh before approving this draft.'})})
    return()=>{alive=false;if(url)URL.revokeObjectURL(url)}
  },[jobId,assetId,key,sha256,onReady])
  return <section className={styles.artAttachment} aria-label="Outreach artwork attachment">
    <h3>Artwork to include</h3>
    <p>The selected file accompanies the first message and any follow-up you approve.</p>
    {loading ? <p role="status">Checking saved artwork.</p> : error ? <div role="alert"><p>Saved artwork could not be read. This does not mean no artwork exists.</p><button className="btn" onClick={onRetry}>Retry saved artwork</button></div> : <>
      {context?.rows.map(row=><SavedArtwork key={`${row.jobId}:${row.revision}:${row.output?.id}:${row.output?.sha256}`} row={row} onChange={onChange}/>)}
      {context && !context.rows.length && <p role="status">No artwork job is linked to this championship.</p>}
      {context && <p><Link className="btn" to={context.addUrl}>Add artwork</Link> · PNG, JPEG or WebP for image artwork; PDF for a CAD handoff brief. Saved originals can be selected immediately. Return to this championship&apos;s compose view after adding versions.</p>}
    </>}
    {!loading && !error && (!options.length ? <p role="status">No supported saved original is available for attachment. Add artwork or inspect the file details above.</p> : <>
      <label>Attachment<select aria-label="Artwork attachment" value={value} onChange={event=>onChange(event.target.value)}>
        <option value="auto">{options.length===1?`Include ${options[0].output.name}`:'Choose the artwork to include'}</option>
        {options.map(row=><option key={row.key} value={row.key}>{row.output.name} · {row.title} · revision {row.revision}</option>)}
        <option value="none">No artwork attachment</option>
      </select></label>
      {selected && <p data-selected-attachment>{selected.output.name} · {Math.ceil(selected.output.bytes/1024)} KB · saved original</p>}
      {preview.key===key && preview.url && (selected?.output.type.startsWith('image/') ? <img className={styles.attachmentPreview} src={preview.url} alt={`Selected artwork: ${selected.output.name}`}/> : <a href={preview.url} target="_blank" rel="noreferrer">Preview {selected?.output.name}</a>)}
      {preview.error && <p role="alert">{preview.error}</p>}
    </>)}
  </section>
}
